import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  ArrowLeft, Check, CornerUpLeft, X, FileText, Loader2,
  ChevronDown, ChevronUp, AlertTriangle, Copy, Plus,
} from 'lucide-react';
import RejectModal from '../shared/RejectModal';
import SendBackModal from '../shared/SendBackModal';
import AuthedReceiptPreview from '../../shared/components/AuthedReceiptPreview';
import ReceiptViewerModal from '../shared/ReceiptViewerModal';
import SearchableSelect from '../../shared/components/SearchableSelect';
import { AMBER, NAVY, BORDER_COLOR, bubbleStyle, selectStyle, fieldStyle, navyBtn, outlineBtn } from '../../styles/tokens';
import { STATUS_COLORS, STATUS_LABEL, PAYMENT_METHODS, PR_TAX_RATES } from '../../../../shared/constants';
import { useOptions } from '../../context/OptionsContext';
import { useAuth, userIsReviewer } from '../../context/AuthContext';
import useSupervisorItems from '../../hooks/useSupervisorItems';
import client from '../../api/client';
import { appInsights } from '../../lib/appInsights';

function fmt(amount) {
  if (amount === null || amount === undefined) return '—';
  return '$' + parseFloat(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function StatusBadge({ status, small }) {
  const s = STATUS_COLORS[status] || STATUS_COLORS.pending;
  return (
    <span style={{
      background: s.bg, color: s.text, border: `1px solid ${s.border}`,
      borderRadius: 20,
      padding: small ? '1px 7px' : '3px 10px',
      fontSize: small ? 10 : 12,
      fontWeight: '600',
      whiteSpace: 'nowrap',
    }}>
      {STATUS_LABEL[status] || status}
    </span>
  );
}

// Derive a receipt-level status from its items (mirrors backend deriveReceiptStatus)
function deriveGroupStatus(items) {
  const statuses = items.map(i => i.status);
  if (statuses.length === 0) return 'pending';
  if (statuses.every(s => s === 'approved')) return 'pending_review';
  if (statuses.every(s => s === 'rejected')) return 'rejected';
  // ALL sent_back → fully returned
  if (statuses.every(s => s === 'sent_back')) return 'sent_back';
  if (statuses.some(s => s === 'sent_back')) return 'partially_returned';
  if (statuses.some(s => s === 'rejected') && statuses.some(s => s === 'pending')) return 'partially_returned';
  if (statuses.some(s => s === 'rejected')) return 'rejected';
  if (statuses.some(s => s === 'approved') && statuses.some(s => s === 'pending')) return 'partially_approved';
  if (statuses.every(s => s === 'pending')) return 'pending';
  return 'pending';
}

const actionBtnStyle = (bg) => ({
  background: bg, color: '#fff', border: 'none',
  borderRadius: 6, padding: '5px 10px', fontSize: 11,
  fontWeight: '600', cursor: 'pointer', fontFamily: 'Inter, sans-serif',
  display: 'flex', alignItems: 'center', gap: 4,
  whiteSpace: 'nowrap',
});

export default function SupervisorDetailPage({ adminMode = false }) {
  const { id: receiptId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const backPath = location.state?.from || (adminMode ? '/admin' : '/supervisor');
  const { glCodes, projects } = useOptions();
  const { user: currentUser } = useAuth();
  const isReviewer = userIsReviewer(currentUser);
  // Who may codify the Código GL: ONLY Revisión Final (reviewer) + admin — same rule as the
  // upload page. Ingeniero/Gerente never see or set GL (they code Tarea + Centro de Costo; the
  // reviewer assigns GL). userIsReviewer already includes admin.
  const canCodeGL = isReviewer;
  const canEditTax = isReviewer;

  // V26 takeover metadata: who last acted on this receipt (badge)
  const [receiptMeta, setReceiptMeta] = useState(null);
  // V29.1: Sage Vendor assignment (reviewer/admin only). Single catalog from sage_vendor_mappings;
  // dropdown filters to credit_card or petty_cash to match the receipt's payment_method.
  const [creditCardVendors, setCreditCardVendors] = useState([]);
  const [pettyCashVendors, setPettyCashVendors] = useState([]);
  const [sageVendorId, setSageVendorId] = useState(null);
  const [sageSaving, setSageSaving] = useState(false);
  const [applyGlobalTax, setApplyGlobalTax] = useState(false);
  const [globalTaxValue, setGlobalTaxValue] = useState('11.5');
  const [items, setItems] = useState([]);
  const [otherSupervisorItems, setOtherSupervisorItems] = useState([]);
  const [expandedOtherSections, setExpandedOtherSections] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [splitItem, setSplitItem] = useState(null); // item being split (modal open)
  const [splitRows, setSplitRows] = useState([]);
  const [viewingItem, setViewingItem] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [sendBackTarget, setSendBackTarget] = useState(null);
  const [dismissedAlerts, setDismissedAlerts] = useState(new Set());

  const {
    handleItemFieldChange,
    handleGlCodeChange,
    handleTaskChange,
    handleProjectChange,
    handleSplitItem,
    handleDeleteItem,
    handleAddItem,
    handleMoveItemToProject,
  } = useSupervisorItems({ items, setItems });

  // Load cost codes (tasks) for task dropdown — keyed by project_id
  const [projectTasks, setProjectTasks] = useState({});
  // Cost centers — keyed by "projectId-costCodeId" for tarea-specific filtering
  const [itemCostCenters, setItemCostCenters] = useState({});
  const [allCostCenters, setAllCostCenters] = useState([]);

  const fetchItems = useCallback(async () => {
    try {
      if (adminMode) {
        // Admin sees all items for the receipt
        const res = await client.get(`/api/receipts/${receiptId}`);
        const receipt = res.data;
        setReceiptMeta({
          lastActorUserId: receipt.lastActorUserId || null,
          lastActorName: receipt.lastActorName || null,
          takeoverTargetName: receipt.takeoverTargetName || null,
          choferId: receipt.userId,
          choferName: receipt.employeeName,
        });
        setSageVendorId(receipt.sageVendorId || null);
        const adminGrandTotal = receipt.receiptTotal != null ? parseFloat(receipt.receiptTotal) : (receipt.amount != null ? parseFloat(receipt.amount) : null);
        const enrichedItems = (receipt.items || []).map(item => ({
          ...item,
          vendor: receipt.vendor,
          receiptDate: receipt.date,
          fileName: receipt.fileName,
          fileType: receipt.fileType,
          previewUrl: receipt.previewUrl,
          paymentMethod: receipt.paymentMethod,
          receiptAmount: adminGrandTotal,
          receiptStatus: receipt.status,
          receiptNumber: receipt.receiptNumber || null,
          employeeName: receipt.employeeName,
          receiptUserId: receipt.userId,
        }));
        setItems(enrichedItems);
      } else if (isReviewer) {
        // Reviewer always sees ALL items on the receipt
        const fullRes = await client.get(`/api/receipts/${receiptId}`);
        const receipt = fullRes.data;
        setReceiptMeta({
          lastActorUserId: receipt.lastActorUserId || null,
          lastActorName: receipt.lastActorName || null,
          takeoverTargetName: receipt.takeoverTargetName || null,
          choferId: receipt.userId,
          choferName: receipt.employeeName,
        });
        setSageVendorId(receipt.sageVendorId || null);
        // For reviewer: use receipt_total (AI grand total) as the validation target
        const grandTotal = receipt.receiptTotal != null ? parseFloat(receipt.receiptTotal) : (receipt.amount != null ? parseFloat(receipt.amount) : null);
        const allItems = (receipt.items || []).map(item => ({
          ...item,
          vendor: receipt.vendor,
          receiptDate: receipt.date,
          fileName: receipt.fileName,
          fileType: receipt.fileType,
          previewUrl: receipt.previewUrl,
          paymentMethod: receipt.paymentMethod,
          receiptAmount: grandTotal,
          receiptStatus: receipt.status,
          receiptNumber: receipt.receiptNumber || null,
          invoiceNumber: receipt.invoiceNumber || null,
          employeeName: receipt.employeeName,
          receiptUserId: receipt.userId,
        }));
        // Reviewer sees all items as their main view (no "other supervisors" split)
        setItems(allItems);
        setOtherSupervisorItems([]);
      } else {
        const res = await client.get('/api/receipt-items/assigned', {
          params: { receiptId },
        });
        setItems(res.data);

        // Fetch receipt-level metadata (lastActor for the takeover badge)
        try {
          const metaRes = await client.get(`/api/receipts/${receiptId}`);
          const r = metaRes.data;
          setReceiptMeta({
            lastActorUserId: r.lastActorUserId || null,
            lastActorName: r.lastActorName || null,
            takeoverTargetName: r.takeoverTargetName || null,
            choferId: r.userId,
            choferName: r.employeeName,
          });
        } catch (metaErr) {
          console.error('Failed to load receipt meta:', metaErr);
        }

        // Fetch all items for cross-supervisor visibility
        try {
          const allRes = await client.get(`/api/receipts/${receiptId}/all-items`);
          const allItems = allRes.data;
          const myId = currentUser?.id;
          // Items already in the main view (own + takeover) — don't duplicate them in the "otros" section.
          const mainViewItemIds = new Set(res.data.map(i => i.id));
          const others = allItems.filter(
            i => i.supervisorId && i.supervisorId !== myId && !mainViewItemIds.has(i.id)
          );
          setOtherSupervisorItems(others);
        } catch (crossErr) {
          console.error('Failed to load cross-supervisor items:', crossErr);
        }
      }
    } catch (err) {
      console.error('Failed to load items:', err);
    } finally {
      setLoading(false);
    }
  }, [receiptId, adminMode, currentUser?.id, isReviewer]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  // V29.1: load Sage Vendor lookup data once per category (reviewer/admin only).
  // Both lists come from sage_vendor_mappings; the dropdown picks the right one based on payment_method.
  useEffect(() => {
    if (!isReviewer && !adminMode) return;
    client.get('/api/vendor-mappings/active', { params: { category: 'credit_card' } })
      .then(r => setCreditCardVendors(r.data || []))
      .catch(err => console.error('Failed to load credit card vendors:', err));
    client.get('/api/vendor-mappings/active', { params: { category: 'petty_cash' } })
      .then(r => setPettyCashVendors(r.data || []))
      .catch(err => console.error('Failed to load petty cash vendors:', err));
  }, [isReviewer, adminMode]);

  async function handleUpdateSageVendor(vendorId) {
    setSageSaving(true);
    try {
      await client.patch(`/api/receipts/${receiptId}`, { sageVendorId: vendorId || '' });
      setSageVendorId(vendorId || null);
    } catch (err) {
      console.error('Failed to update Sage Vendor ID:', err);
      alert(err.response?.data?.error || 'Error al guardar el Proveedor Sage.');
    } finally {
      setSageSaving(false);
    }
  }

  // Fetch cost codes (tasks) for each unique project in the items (for Task dropdown)
  useEffect(() => {
    const projectIds = [...new Set(items.map(i => i.projectId).filter(Boolean))];
    const missing = projectIds.filter(pid => !projectTasks[pid]);
    if (missing.length === 0) return;
    missing.forEach(pid => {
      client.get('/api/nomina/cost-codes', { params: { project_id: pid } })
        .then(res => setProjectTasks(prev => ({ ...prev, [pid]: res.data || [] })))
        .catch(() => setProjectTasks(prev => ({ ...prev, [pid]: [] })));
    });
  }, [items]);

  // Fetch cost centers (all + per tarea/project) — for all editors
  useEffect(() => {
    // Fetch all cost centers once
    client.get('/api/admin/cost-centers')
      .then(r => setAllCostCenters((r.data || []).filter(cc => cc.active !== false)))
      .catch(() => {});
    // Fetch per-tarea cost centers for each unique project+tarea combo
    const combos = [...new Set(items.filter(i => i.projectId && i.selectedTaskId).map(i => `${i.projectId}-${i.selectedTaskId}`))];
    combos.forEach(key => {
      if (itemCostCenters[key]) return;
      const [pid, ccid] = key.split('-');
      client.get(`/api/admin/projects/${pid}/cost-centers-for-tarea`, { params: { cost_code_id: ccid } })
        .then(r => setItemCostCenters(prev => ({ ...prev, [key]: r.data || [] })))
        .catch(() => {});
    });
  }, [items]);

  // Build the receipt group from items
  const group = useMemo(() => {
    if (items.length === 0) return null;
    const first = items[0];
    return {
      receiptId: first.receiptId,
      vendor: first.vendor,
      employeeName: first.employeeName,
      receiptDate: first.receiptDate,
      receiptNumber: first.receiptNumber,
      invoiceNumber: first.invoiceNumber,
      fileName: first.fileName,
      fileType: first.fileType,
      previewUrl: first.previewUrl,
      paymentMethod: first.paymentMethod,
      cardType: first.cardType || null,
      receiptAmount: first.receiptAmount,
      receiptStatus: first.receiptStatus,
      reviewedBy: first.reviewedBy,
      reviewerName: first.reviewerName,
      items,
      totalAmount: items.reduce((s, i) => { const a=parseFloat(i.amount)||0; return s+a+a*(parseFloat(i.taxRate)||0)/100; }, 0),
      // Use server's receipt status as authoritative — reflects reviewer approvals correctly
      status: first.receiptStatus || deriveGroupStatus(items),
    };
  }, [items]);

  const hasQuantity = group?.items?.some(i => (i.quantity || 1) > 1);
  const gridCols = [
    '1fr', '180px',
    '100px',                            // Cost Center (after Tarea, all editors)
    ...(canCodeGL ? ['100px'] : []),    // GL Code (solo Revisión Final / Admin, after CC)
    '45px',                             // Quantity
    '55px',                             // Unit
    '70px',                             // Unit Price
    '90px',                             // Amount
    ...(canEditTax ? ['55px', '60px'] : []),  // Tax%, Tax$ (reviewer only)
    '80px', '70px', '56px',
  ].join(' ');

  // Group other supervisors' items by supervisor
  const otherSupervisorGroups = useMemo(() => {
    const map = new Map();
    for (const item of otherSupervisorItems) {
      const sid = item.supervisorId;
      const sname = item.supervisorName || '(Desconocido)';
      if (!map.has(sid)) map.set(sid, { supervisorId: sid, supervisorName: sname, items: [] });
      map.get(sid).items.push(item);
    }
    return Array.from(map.values());
  }, [otherSupervisorItems]);

  function toggleOtherSection(supervisorId) {
    setExpandedOtherSections(prev => {
      const next = new Set(prev);
      if (next.has(supervisorId)) next.delete(supervisorId);
      else next.add(supervisorId);
      return next;
    });
  }

  // ── Receipt-level actions ──
  // For reviewer: pending items are (1) admin items assigned to them that are pending, OR (2) all items when receipt is pending_review
  // For engineer/manager/supervisor: their own pending items PLUS takeover items (V26 — backend
  // only returns takeover items to senior-on-ganga / project-reviewer users, so trust the response).
  const pendingItems = group ? (
    isReviewer
      ? (group.receiptStatus === 'pending_review'
          ? group.items.filter(i => i.status === 'approved')  // reviewer final review: acts on approved items
          : group.items.filter(i => i.status === 'pending'))  // V26: reviewer/admin takeover on pending items
      : group.items.filter(i => i.status === 'pending')        // own + takeover (backend-gated)
  ) : [];
  const hasPendingItems = pendingItems.length > 0 || (isReviewer && group?.receiptStatus === 'pending_review');

  async function handleReceiptApprove() {
    if (!group) return;

    if (adminMode) {
      const confirmed = window.confirm('Está aprobando gastos asignados a otro supervisor. ¿Desea continuar?');
      if (!confirmed) return;
    }

    // Only validate pending items (approved/rejected items stay untouched)
    // Validate Tarea (project items) or GL Code (admin items)
    const missingTaskOrGL = pendingItems.filter(i => {
      const isAdmin = i.expenseType === 'admin' || i.isAdminProject;
      return isAdmin ? !i.glCode : !i.selectedTaskId;
    });
    if (missingTaskOrGL.length > 0) {
      const details = missingTaskOrGL.map(i => {
        const isAdmin = i.expenseType === 'admin' || i.isAdminProject;
        return `"${i.description || '(sin descripción)'}": falta ${isAdmin ? 'Código GL' : 'Tarea'}`;
      });
      alert(`No se puede aprobar sin completar:\n\n${details.join('\n')}`);
      return;
    }

    const missingProject = pendingItems.filter(i => !i.projectId);
    if (missingProject.length > 0) {
      const details = missingProject.map(i => `"${i.description || '(sin descripción)'}": falta Proyecto`);
      alert(`No se puede aprobar sin completar:\n\n${details.join('\n')}`);
      return;
    }

    // Validate Cost Center on all non-admin items
    const missingCC = pendingItems.filter(i => {
      const isAdmin = i.expenseType === 'admin' || i.isAdminProject;
      return !isAdmin && !i.costCenter;
    });
    if (missingCC.length > 0) {
      const details = missingCC.map(i => `"${i.description || '(sin descripción)'}": falta Centro de Costo`);
      alert(`No se puede aprobar sin completar:\n\n${details.join('\n')}`);
      return;
    }

    // Validate split groups: items split from the same parent must sum to the original amount
    const splitGroups = {};
    for (const item of group.items) {
      const parentId = item.splitFromId || item.id;
      if (!splitGroups[parentId]) splitGroups[parentId] = { items: [], originalAmount: null };
      splitGroups[parentId].items.push(item);
      if (item.originalAmount) splitGroups[parentId].originalAmount = item.originalAmount;
    }
    for (const [parentId, sg] of Object.entries(splitGroups)) {
      if (sg.items.length > 1 && sg.originalAmount) {
        const splitSum = sg.items.reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);
        if (Math.abs(splitSum - sg.originalAmount) > 0.01) {
          alert(`Los items divididos de "${sg.items[0].description}" suman $${splitSum.toFixed(2)} pero deberían sumar $${sg.originalAmount.toFixed(2)}.\n\nAjuste los montos antes de aprobar.`);
          return;
        }
      }
    }

    // Validate: items total must match the fixed receipt total (AI-extracted, never changes)
    const otherTotal = otherSupervisorItems.reduce((s, i) => {
      const a = parseFloat(i.amount) || 0;
      return s + a + a * (parseFloat(i.taxRate) || 0) / 100;
    }, 0);
    const myTotal = group.items.reduce((s, i) => {
      const a = parseFloat(i.amount) || 0;
      return s + a + a * (parseFloat(i.taxRate) || 0) / 100;
    }, 0);
    const fixedReceiptTotal = group.receiptAmount || 0;
    if (fixedReceiptTotal > 0) {
      const assignedPortion = fixedReceiptTotal - otherTotal;
      if (Math.abs(myTotal - assignedPortion) > 0.02) {
        alert(
          `El total de sus items (Mis Items: $${myTotal.toFixed(2)}) no coincide con el Total Asignado ($${assignedPortion.toFixed(2)}).\n\nTotal del Recibo: $${fixedReceiptTotal.toFixed(2)}\nOtros supervisores: $${otherTotal.toFixed(2)}\nTotal Asignado: $${assignedPortion.toFixed(2)}\n\nAjuste los montos antes de aprobar.`
        );
        return;
      }
    }

    setActionLoading('approve');
    try {
      await client.patch(`/api/receipts/${receiptId}/status`, { status: 'approved', ...(adminMode && { adminOverride: true }) });
      await fetchItems();
    } catch (err) {
      console.error('Receipt approve error:', err);
      appInsights?.trackException({
        exception: err,
        properties: {
          context: 'gastos_receipt_approve',
          receipt_id: receiptId,
          admin_mode: !!adminMode,
        },
      });
      alert(err.response?.data?.error || 'Error al aprobar recibo.');
    } finally {
      setActionLoading(null);
    }
  }

  function handleReceiptSendBack() {
    if (!group) return;
    setSendBackTarget({
      id: group.receiptId,
      description: group.vendor || `Recibo #${group.receiptId}`,
      _isReceipt: true,
    });
  }

  function handleReceiptReject() {
    if (!group) return;
    setRejectTarget({
      id: group.receiptId,
      description: group.vendor || `Recibo #${group.receiptId}`,
      _isReceipt: true,
    });
  }

  async function handleConfirmReject({ reason, notes }) {
    const target = rejectTarget;
    setRejectTarget(null);
    if (!target) return;

    setActionLoading('reject');
    try {
      if (target._isReceipt) {
        await client.patch(`/api/receipts/${target.id}/status`, {
          status: 'rejected', review_reason: reason, review_notes: notes || null,
          ...(adminMode && { adminOverride: true }),
        });
      } else {
        await client.patch(`/api/receipt-items/${target.id}/status`, {
          status: 'rejected', review_reason: reason, review_notes: notes || null,
        });
      }
      await fetchItems();
    } catch (err) {
      console.error('Reject error:', err);
      appInsights?.trackException({
        exception: err,
        properties: {
          context: 'gastos_receipt_reject',
          receipt_id: target.id,
          is_receipt: !!target._isReceipt,
        },
      });
      alert(err.response?.data?.error || 'Error al rechazar.');
    } finally {
      setActionLoading(null);
    }
  }

  async function handleConfirmSendBack({ reason, notes }) {
    const target = sendBackTarget;
    if (!target) return;
    setSendBackTarget(null);

    setActionLoading('sendback');
    try {
      if (target._isReviewerAction) {
        await client.patch(`/api/receipts/${target.id}/reviewer-approve`, {
          status: 'sent_back', review_reason: reason, review_notes: notes || null,
        });
        // Reviewer returned receipt → navigate back to list
        navigate(backPath);
        return;
      } else if (target._isReceipt) {
        await client.patch(`/api/receipts/${target.id}/status`, {
          status: 'sent_back', review_reason: reason, review_notes: notes || null,
          ...(adminMode && { adminOverride: true }),
        });
      } else {
        await client.patch(`/api/receipt-items/${target.id}/status`, {
          status: 'sent_back', review_reason: reason, review_notes: notes || null,
        });
      }
      await fetchItems();
    } catch (err) {
      console.error('Send back error:', err);
      appInsights?.trackException({
        exception: err,
        properties: {
          context: 'gastos_receipt_send_back',
          receipt_id: target.id,
          is_receipt: !!target._isReceipt,
        },
      });
      alert(err.response?.data?.error || 'Error al devolver.');
    } finally {
      setActionLoading(null);
    }
  }

  // ── Reviewer actions (for pending_review receipts) ──
  async function handleReviewerApprove() {
    if (!group) return;

    // Validate: items total (with tax) must match the stored receipt total
    const itemsWithTax = group.items.reduce((s, i) => {
      const a = parseFloat(i.amount) || 0;
      return s + a + a * (parseFloat(i.taxRate) || 0) / 100;
    }, 0);
    const storedTotal = group.receiptAmount || 0;

    if (storedTotal > 0 && Math.abs(itemsWithTax - storedTotal) > 0.02) {
      alert(
        `El total de los items con impuestos ($${itemsWithTax.toFixed(2)}) no coincide con el Total del Recibo ($${storedTotal.toFixed(2)}).\n\nVerifique los impuestos antes de aprobar.`
      );
      return;
    }

    setActionLoading('reviewer-approve');
    try {
      await client.patch(`/api/receipts/${receiptId}/reviewer-approve`, { status: 'approved' });
      navigate(backPath);
    } catch (err) {
      console.error('Reviewer approve error:', err);
      appInsights?.trackException({
        exception: err,
        properties: {
          context: 'gastos_reviewer_approve',
          receipt_id: receiptId,
        },
      });
      alert(err.response?.data?.error || 'Error al aprobar.');
    } finally {
      setActionLoading(null);
    }
  }

  function handleReviewerSendBack() {
    if (!group) return;
    setSendBackTarget({
      id: group.receiptId,
      description: group.vendor || `Recibo #${group.receiptId}`,
      _isReviewerAction: true,
    });
  }


  function dismissAlert() {
    setDismissedAlerts(prev => new Set(prev).add(receiptId));
  }

  // ── Project Summary helper ──
  function renderProjectSummary(projectItems) {
    const isAdminGroup = projectItems.some(i => i.expenseType === 'admin' || i.isAdminProject);
    const subTotal = projectItems.reduce((s, i) => {
      const a = parseFloat(i.amount) || 0;
      return s + a + a * (parseFloat(i.taxRate) || 0) / 100;
    }, 0);

    // === Table 1: Tarea × Centro de Costo matrix (project items) or GL list (admin items) ===
    const matrix = {};
    const costCenterKeys = new Set();
    for (const item of projectItems) {
      const tareaKey = isAdminGroup
        ? (item.glCode || '(Sin GL)')
        : (item.selectedTaskName || '(Sin Tarea)');
      // Admin items: no cost center dimension, just GL as single key
      const ccKey = isAdminGroup ? '—' : (item.costCenter || '(Sin CC)');
      const amt = parseFloat(item.amount) || 0;
      const tax = amt * (parseFloat(item.taxRate) || 0) / 100;
      if (!matrix[tareaKey]) matrix[tareaKey] = {};
      matrix[tareaKey][ccKey] = (matrix[tareaKey][ccKey] || 0) + amt + tax;
      costCenterKeys.add(ccKey);
    }
    const tareas = Object.keys(matrix).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
    const ccCols = [...costCenterKeys].sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
    const showMatrix = ccCols.length > 1 || (ccCols.length === 1 && ccCols[0] !== '(Sin CC)');

    // === Table 2: GL summary (reviewer only) ===
    const byGL = {};
    if (isReviewer && !isAdminGroup) {
      for (const item of projectItems) {
        const key = item.glCode || '(Sin GL)';
        const amt = parseFloat(item.amount) || 0;
        const tax = amt * (parseFloat(item.taxRate) || 0) / 100;
        if (!byGL[key]) byGL[key] = 0;
        byGL[key] += amt + tax;
      }
    }
    const glEntries = Object.entries(byGL).sort((a, b) => a[0].localeCompare(b[0], undefined, { numeric: true }));

    const colTotals = {};
    ccCols.forEach(cc => { colTotals[cc] = 0; });

    return (
      <div style={{ padding: '10px 18px', background: '#f8fafc', borderTop: `1px solid ${BORDER_COLOR}`, overflowX: 'auto' }}>
        {/* Table 1: Tarea × Centro de Costo */}
        {showMatrix ? (
          <>
            <div style={{ fontWeight: 600, color: NAVY, marginBottom: 8, fontSize: 12 }}>
              {isAdminGroup ? 'Resumen por Código GL' : 'Resumen Tarea × Centro de Costo'}:
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, marginBottom: 16 }}>
              <thead>
                <tr style={{ borderBottom: `2px solid ${BORDER_COLOR}` }}>
                  <th style={{ textAlign: 'left', padding: '4px 6px', color: '#64748b', fontWeight: 600 }}>
                    {isAdminGroup ? 'GL' : 'Tarea'}
                  </th>
                  {ccCols.map(cc => (
                    <th key={cc} style={{ textAlign: 'right', padding: '4px 6px', color: '#64748b', fontWeight: 600 }}>{cc}</th>
                  ))}
                  <th style={{ textAlign: 'right', padding: '4px 6px', color: NAVY, fontWeight: 700 }}>Total</th>
                </tr>
              </thead>
              <tbody>
                {tareas.map(t => {
                  let rowTotal = 0;
                  return (
                    <tr key={t} style={{ borderBottom: `1px solid ${BORDER_COLOR}` }}>
                      <td style={{ padding: '4px 6px', fontWeight: 600, color: NAVY }}>{t}</td>
                      {ccCols.map(cc => {
                        const val = matrix[t][cc] || 0;
                        rowTotal += val;
                        colTotals[cc] += val;
                        return (
                          <td key={cc} style={{ padding: '4px 6px', textAlign: 'right', color: val > 0 ? '#475569' : '#cbd5e1' }}>
                            {val > 0 ? fmt(val) : '—'}
                          </td>
                        );
                      })}
                      <td style={{ padding: '4px 6px', textAlign: 'right', fontWeight: 700, color: NAVY }}>{fmt(rowTotal)}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr style={{ borderTop: `2px solid ${BORDER_COLOR}` }}>
                  <td style={{ padding: '4px 6px', fontWeight: 700, color: NAVY }}>Total</td>
                  {ccCols.map(cc => (
                    <td key={cc} style={{ padding: '4px 6px', textAlign: 'right', fontWeight: 700, color: NAVY }}>{fmt(colTotals[cc])}</td>
                  ))}
                  <td style={{ padding: '4px 6px', textAlign: 'right', fontWeight: 700, color: NAVY }}>{fmt(subTotal)}</td>
                </tr>
              </tfoot>
            </table>
          </>
        ) : (
          <>
            <div style={{ fontWeight: 600, color: NAVY, marginBottom: 6, fontSize: 12 }}>
              Resumen por {isAdminGroup ? 'Código GL' : 'Tarea'}:
            </div>
            {tareas.map(t => (
              <div key={t} style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0', color: '#475569', fontSize: 12 }}>
                <span>{t}</span>
                <span style={{ fontWeight: 600 }}>{fmt(Object.values(matrix[t]).reduce((s, v) => s + v, 0))}</span>
              </div>
            ))}
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', marginTop: 4, borderTop: `1px solid ${BORDER_COLOR}`, fontWeight: 700, color: NAVY, fontSize: 12, marginBottom: 16 }}>
              <span>Total</span><span>{fmt(subTotal)}</span>
            </div>
          </>
        )}

        {/* Table 2: GL summary — reviewer only, separate from Tarea×CC */}
        {glEntries.length > 0 && (
          <>
            <div style={{ fontWeight: 600, color: NAVY, marginBottom: 6, fontSize: 12 }}>
              Resumen por Código GL:
            </div>
            {glEntries.map(([code, total]) => (
              <div key={code} style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0', color: '#475569', fontSize: 12, maxWidth: 350 }}>
                <span>{code}</span>
                <span style={{ fontWeight: 600 }}>{fmt(total)}</span>
              </div>
            ))}
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', marginTop: 4, borderTop: `1px solid ${BORDER_COLOR}`, fontWeight: 700, color: NAVY, fontSize: 12, maxWidth: 350 }}>
              <span>Total</span>
              <span>{fmt(subTotal)}</span>
            </div>
          </>
        )}
      </div>
    );
  }

  // ── Item Row renderer ──
  function renderItemRow(item) {
    const isPending = item.status === 'pending';
    const isApproved = item.status === 'approved';
    const isMyItem = item.supervisorId === currentUser?.id;
    const isAdminItem = item.expenseType === 'admin' || item.isAdminProject;
    // Reviewer editable logic:
    //   - pending_review receipt: project items editable (for GL), admin items already reviewed = locked
    //   - non-pending_review: only reviewer's own pending admin items editable
    // Engineer/Supervisor: editable when item is still pending
    // V26: reviewer/admin can also edit pending items during takeover (backend enforces
    // that they're the project's expenses_reviewer or admin). Manager-on-ganga falls
    // through the non-reviewer branch and gets `isPending` automatically.
    const isEditable = isReviewer
      ? (group?.receiptStatus === 'pending_review'
          // pending_review: engineer-approved items stay editable until final approval,
          // AND reviewer-added items (status 'pending') must be editable too — otherwise a
          // freshly "+ Agregar Item" row locks all its fields to "—" (Bug 2).
          ? (isApproved || isPending)
          : isPending)  // non-pending_review: reviewer/admin can edit any pending item (takeover)
      : isPending;
    const canModifyStructure = isReviewer
      ? isPending
      : isPending;
    const projectNames = group ? [...new Set(group.items.map(i => i.projectName).filter(Boolean))] : [];
    const multipleProjects = projectNames.length > 1;

    return (
      <div
        key={item.id}
        style={{
          display: 'grid',
          gridTemplateColumns: gridCols,
          padding: '10px 18px', gap: 8,
          borderBottom: '1px solid #f1f5f9',
          alignItems: 'center',
          background: isPending && (!item.projectId || ((item.expenseType === 'admin' || item.isAdminProject) ? !item.glCode : !item.selectedTaskId)) ? '#fffbeb' : '#fff',
        }}
      >
        {/* Description */}
        <div>
          {isEditable ? (
            <div>
              <input
                style={{ ...fieldStyle, fontWeight: '600', fontSize: 13, color: '#1e293b' }}
                value={item.description || ''}
                onChange={(e) => handleItemFieldChange(item.id, 'description', e.target.value)}
                placeholder="Descripción"
              />
              <div style={{ display: 'flex', gap: 4, marginTop: 3 }}>
                {item.isPersonal && (
                  <span style={{ background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', borderRadius: 12, padding: '1px 7px', fontSize: 9, fontWeight: 600 }}>Personal</span>
                )}
                {item.expenseType === 'admin' && (
                  <span style={{ background: '#fef3c7', color: '#92400e', border: '1px solid #fcd34d', borderRadius: 12, padding: '1px 7px', fontSize: 9, fontWeight: 600 }}>Admin</span>
                )}
                {!isReviewer && !isMyItem && isPending && (
                  <span title={`Asignado originalmente a ${item.supervisorName || 'otro ingeniero'}`} style={{ background: '#fef3c7', color: '#92400e', border: '1px solid #fde68a', borderRadius: 12, padding: '1px 7px', fontSize: 9, fontWeight: 600 }}>Takeover</span>
                )}
              </div>
            </div>
          ) : (
            <div>
              <div style={{ fontWeight: '600', fontSize: 13, color: '#1e293b', display: 'flex', alignItems: 'center', gap: 6 }}>
                {item.description || '—'}
                {item.isPersonal && (
                  <span style={{ background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', borderRadius: 12, padding: '1px 7px', fontSize: 9, fontWeight: 600 }}>Personal</span>
                )}
                {item.expenseType === 'admin' && (
                  <span style={{ background: '#fef3c7', color: '#92400e', border: '1px solid #fcd34d', borderRadius: 12, padding: '1px 7px', fontSize: 9, fontWeight: 600 }}>Admin</span>
                )}
                {!isReviewer && !isMyItem && isPending && (
                  <span title={`Asignado originalmente a ${item.supervisorName || 'otro ingeniero'}`} style={{ background: '#fef3c7', color: '#92400e', border: '1px solid #fde68a', borderRadius: 12, padding: '1px 7px', fontSize: 9, fontWeight: 600 }}>Takeover</span>
                )}
              </div>
              {(item.status === 'sent_back' || item.status === 'rejected') && item.reviewReason && (
                <div style={{ fontSize: 11, color: item.status === 'rejected' ? '#ef4444' : '#d97706', marginTop: 2, display: 'flex', alignItems: 'center', gap: 3 }}>
                  <CornerUpLeft size={10} /> {item.reviewReason}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Tarea (project items) or Código GL (administrative items) */}
        <div>
          {item.expenseType === 'admin' || item.isAdminProject ? (
            // Admin items → GL Code dropdown
            isEditable ? (
              <select
                style={{ ...selectStyle, fontSize: 11 }}
                value={item.glCode || ''}
                onChange={(e) => handleGlCodeChange(item.id, e.target.value)}
              >
                <option value="">— Código GL —</option>
                {glCodes.map(g => <option key={g.code} value={g.code}>{g.code}</option>)}
              </select>
            ) : (
              <span style={{ fontSize: 12, color: '#1e293b', fontWeight: '500' }}>
                {item.glCode || '—'}
              </span>
            )
          ) : (
            // Project items → Task dropdown
            isEditable ? (
              <select
                style={{
                  ...selectStyle, fontSize: 11,
                  ...(isPending && !item.selectedTaskId ? { border: `2px solid ${AMBER}`, background: '#fffbeb' } : {}),
                }}
                value={item.selectedTaskId || ''}
                onChange={(e) => {
                  const taskId = e.target.value;
                  const tasks = projectTasks[item.projectId] || [];
                  const task = tasks.find(t => String(t.id) === String(taskId));
                  handleTaskChange(item.id, taskId || null, task ? `${task.code} - ${task.description}` : null);
                }}
              >
                <option value="">— Tarea —</option>
                {(projectTasks[item.projectId] || []).map(t => (
                  <option key={t.id} value={t.id}>{t.code} - {t.description}</option>
                ))}
              </select>
            ) : (
              <span style={{ fontSize: 12, color: '#1e293b', fontWeight: '500' }}>
                {item.selectedTaskName || '—'}
              </span>
            )
          )}
        </div>

        {/* Cost Center column — hidden for admin items (they use GL instead) */}
        <div>
          {(item.expenseType === 'admin' || item.isAdminProject) ? (
            <span style={{ fontSize: 11, color: '#94a3b8' }}>—</span>
          ) : isEditable ? (
            <select
              style={{
                ...selectStyle, fontSize: 11,
                ...(isPending && !item.costCenter ? { border: `2px solid ${AMBER}`, background: '#fffbeb' } : {}),
              }}
              value={item.costCenter || ''}
              onChange={(e) => handleItemFieldChange(item.id, 'costCenter', e.target.value)}
            >
              <option value="">— CC —</option>
              {(itemCostCenters[`${item.projectId}-${item.selectedTaskId}`] || allCostCenters).map(cc => (
                <option key={cc.id} value={cc.name}>{cc.name}</option>
              ))}
            </select>
          ) : (
            <span style={{ fontSize: 12, color: '#1e293b', fontWeight: '500' }}>
              {item.costCenter || '—'}
            </span>
          )}
        </div>

        {/* GL Code column — visible solo a Revisión Final / Admin (after Cost Center) */}
        {canCodeGL && (
          <div>
            {isEditable ? (
              <select
                style={{ ...selectStyle, fontSize: 11 }}
                value={item.glCode || ''}
                onChange={(e) => handleGlCodeChange(item.id, e.target.value)}
              >
                <option value="">— GL —</option>
                {glCodes.map(g => <option key={g.code} value={g.code}>{g.code}</option>)}
              </select>
            ) : (
              <span style={{ fontSize: 12, color: '#1e293b', fontWeight: '500' }}>
                {item.glCode || '—'}
              </span>
            )}
          </div>
        )}

        {/* Quantity */}
        <div>
          {isEditable ? (
            <input
              style={{ ...fieldStyle, fontSize: 12, textAlign: 'center', width: '100%' }}
              value={item.quantity ?? 1}
              onChange={(e) => {
                const qty = parseInt(e.target.value) || 1;
                handleItemFieldChange(item.id, 'quantity', qty);
              }}
              type="number" step="1" min="1"
            />
          ) : (
            <div style={{ textAlign: 'center', fontSize: 12, color: '#475569', fontWeight: 600 }}>{item.quantity || 1}</div>
          )}
        </div>

        {/* Unit */}
        <div>
          {isEditable ? (
            <input
              style={{ ...fieldStyle, fontSize: 11, width: '100%' }}
              value={item.unit ?? ''}
              onChange={(e) => handleItemFieldChange(item.id, 'unit', e.target.value)}
              placeholder="—"
            />
          ) : (
            <div style={{ fontSize: 11, color: '#64748b' }}>{item.unit || '—'}</div>
          )}
        </div>

        {/* Unit Price (amount / quantity) — read-only */}
        <div style={{ fontWeight: 500, color: '#64748b', fontSize: 12, textAlign: 'right' }}>
          {fmt((parseFloat(item.amount) || 0) / (item.quantity || 1))}
        </div>

        {/* Monto (line total) */}
        <div>
          {isEditable ? (
            <input
              style={{ ...fieldStyle, fontWeight: '700', fontSize: 13, color: '#1e293b', textAlign: 'right' }}
              value={item.amount ?? ''}
              onChange={(e) => handleItemFieldChange(item.id, 'amount', e.target.value)}
              type="number" step="0.01" min="0"
              placeholder="0.00"
            />
          ) : (
            <div style={{ fontWeight: '700', color: '#1e293b', fontSize: 13, textAlign: 'right' }}>
              {fmt(item.amount)}
            </div>
          )}
        </div>

        {/* Tax % — reviewer only */}
        {canEditTax && (
          <div>
            {isEditable ? (
              <div>
                <select
                  style={{ ...selectStyle, fontSize: 11, width: 80 }}
                  value={PR_TAX_RATES.some(r => r.value === parseFloat(item.taxRate)) ? String(item.taxRate) : 'custom'}
                  onChange={(e) => {
                    if (e.target.value === 'custom') {
                      handleItemFieldChange(item.id, 'taxRate', '');
                    } else {
                      handleItemFieldChange(item.id, 'taxRate', e.target.value);
                    }
                  }}
                >
                  {PR_TAX_RATES.map(r => <option key={r.value} value={String(r.value)}>{r.value}%</option>)}
                  <option value="custom">Otro…</option>
                </select>
                {!PR_TAX_RATES.some(r => r.value === parseFloat(item.taxRate)) && (
                  <input
                    style={{ ...fieldStyle, fontSize: 11, width: 55, marginTop: 2, textAlign: 'right' }}
                    value={item.taxRate ?? ''}
                    onChange={(e) => handleItemFieldChange(item.id, 'taxRate', e.target.value)}
                    type="number" step="0.01" min="0" placeholder="%"
                  />
                )}
              </div>
            ) : (
              <div style={{ fontSize: 12, color: '#64748b', textAlign: 'right' }}>
                {item.taxRate > 0 ? `${item.taxRate}%` : '0%'}
              </div>
            )}
          </div>
        )}

        {/* Tax $ — reviewer only */}
        {canEditTax && (
          <div style={{ fontSize: 12, color: '#64748b', textAlign: 'right', fontWeight: 500 }}>
            {(() => { const a = parseFloat(item.amount) || 0; const t = a * (parseFloat(item.taxRate) || 0) / 100; return t > 0 ? fmt(t) : '—'; })()}
          </div>
        )}

        {/* Total (amount + tax) */}
        <div style={{ fontSize: 12, fontWeight: 700, color: NAVY, textAlign: 'right' }}>
          {(() => { const a = parseFloat(item.amount) || 0; return fmt(a + a * (parseFloat(item.taxRate) || 0) / 100); })()}
        </div>

        {/* Status + Personal badge */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'flex-start' }}>
          <StatusBadge status={item.status} small />
          {item.isPersonal && (
            <span style={{ background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', borderRadius: 10, padding: '1px 6px', fontSize: 9, fontWeight: 600, whiteSpace: 'nowrap' }}>Personal</span>
          )}
          {item.isPaid && (
            <span style={{ background: '#dcfce7', color: '#166534', border: '1px solid #bbf7d0', borderRadius: 12, padding: '1px 7px', fontSize: 9, fontWeight: 600, whiteSpace: 'nowrap' }}>Pagado</span>
          )}
        </div>

        {/* Actions — structural only (no split/move/delete for reviewer) */}
        <div style={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {canModifyStructure && multipleProjects && (
            <>
              <button
                onClick={() => handleMoveItemToProject(item, -1, group)}
                title="Mover a proyecto anterior"
                style={{
                  background: 'none', border: `1px solid ${BORDER_COLOR}`, borderRadius: 4,
                  padding: '2px 4px', cursor: 'pointer', color: '#64748b', display: 'flex',
                }}
              >
                <ChevronUp size={11} />
              </button>
              <button
                onClick={() => handleMoveItemToProject(item, +1, group)}
                title="Mover a proyecto siguiente"
                style={{
                  background: 'none', border: `1px solid ${BORDER_COLOR}`, borderRadius: 4,
                  padding: '2px 4px', cursor: 'pointer', color: '#64748b', display: 'flex',
                }}
              >
                <ChevronDown size={11} />
              </button>
            </>
          )}
          {canModifyStructure && (
            <button
              onClick={() => {
                const amt = parseFloat(item.amount) || 0;
                setSplitItem(item);
                setSplitRows([
                  { pct: 50, amount: Math.round(amt * 50) / 100, costCenter: item.costCenter || '' },
                  { pct: 50, amount: Math.round(amt * 50) / 100, costCenter: '' },
                ]);
              }}
              title="Dividir item"
              style={{
                background: 'none', border: `1px solid ${BORDER_COLOR}`, borderRadius: 4,
                padding: '3px 6px', cursor: 'pointer', fontSize: 10, color: '#64748b',
                display: 'flex', alignItems: 'center', gap: 3,
              }}
            >
              <Copy size={11} />
            </button>
          )}
          {canModifyStructure && group && group.items.length > 1 && (
            <button
              onClick={() => handleDeleteItem(item, group)}
              title="Eliminar item"
              style={{
                background: 'none', border: '1px solid #fecaca', borderRadius: 4,
                padding: '3px 6px', cursor: 'pointer', fontSize: 10, color: '#ef4444',
                display: 'flex', alignItems: 'center', gap: 3,
              }}
            >
              <X size={11} />
            </button>
          )}
        </div>
      </div>
    );
  }

  // Loading state
  if (loading) {
    return (
      <div className="page-pad" style={{ padding: '24px 28px', maxWidth: 1200, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 80, color: '#94a3b8', gap: 8 }}>
          <Loader2 size={20} className="spin" /> Cargando recibo…
        </div>
      </div>
    );
  }

  if (!group) {
    return (
      <div className="page-pad" style={{ padding: '24px 28px', maxWidth: 1200, margin: '0 auto' }}>
        <button onClick={() => navigate(backPath)} style={{
          background: 'transparent', border: `1px solid ${BORDER_COLOR}`,
          color: '#475569', borderRadius: 8, padding: '7px 14px',
          fontWeight: '600', fontSize: 13, cursor: 'pointer',
          fontFamily: 'Inter, sans-serif', display: 'flex', alignItems: 'center', gap: 6,
        }}>
          <ArrowLeft size={14} /> Regresar
        </button>
        <div style={{ textAlign: 'center', padding: '60px 20px', color: '#94a3b8' }}>
          <div style={{ fontSize: 16, fontWeight: '600', color: '#64748b' }}>Recibo no encontrado</div>
        </div>
      </div>
    );
  }

  const { vendor, receiptDate, receiptNumber, invoiceNumber, previewUrl, fileType, paymentMethod, totalAmount, status: groupStatus, employeeName, reviewedBy, reviewerName } = group;
  const projectNames = [...new Set(group.items.map(i => i.projectName).filter(Boolean))];
  // V29.1: receipts in terminal states cannot be edited — no send-back path exists.
  const isFinalized = ['approved', 'paid'].includes(groupStatus);

  // Group items by project
  const itemsByProject = {};
  for (const item of group.items) {
    const key = item.projectName || '(Sin Proyecto)';
    if (!itemsByProject[key]) itemsByProject[key] = [];
    itemsByProject[key].push(item);
  }
  const projectGroups = Object.entries(itemsByProject);

  // 03/06 alert (based on cost center — kept for backward compat)
  const hasAlertCodes = group.items.some(item => {
    const code = (item.costCenter || '').trim();
    return code.startsWith('03') || code.startsWith('06');
  });

  // Receipt viewer modal data
  const viewerReceipt = viewingItem ? {
    id: viewingItem.receiptId,
    vendor: viewingItem.vendor,
    employeeName: viewingItem.employeeName,
    status: viewingItem.status,
    amount: viewingItem.amount,
    date: viewingItem.receiptDate,
    fileName: viewingItem.fileName,
    fileType: viewingItem.fileType,
    previewUrl: viewingItem.previewUrl,
  } : null;

  return (
    <div className="page-pad" style={{ padding: '24px 28px', maxWidth: 1200, margin: '0 auto', animation: 'fadeUp 0.3s ease' }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 20 }}>
        <button onClick={() => navigate(backPath)} style={{
          background: 'transparent', border: `1px solid ${BORDER_COLOR}`,
          color: '#475569', borderRadius: 8, padding: '7px 14px',
          fontWeight: '600', fontSize: 13, cursor: 'pointer',
          fontFamily: 'Inter, sans-serif', display: 'flex', alignItems: 'center', gap: 6,
        }}>
          <ArrowLeft size={14} /> Regresar
        </button>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <h2 style={{ fontSize: 18, fontWeight: '700', color: NAVY, margin: 0 }}>
              {receiptNumber ? `#${receiptNumber}` : 'Recibo'}
              {invoiceNumber ? ` · Factura: ${invoiceNumber}` : ''}
              {' — '}
              {vendor || '(Sin proveedor)'}
            </h2>
            <StatusBadge status={groupStatus} />
            {paymentMethod === 'credit_card' && group.cardType === 'total' && (
              <span style={{
                background: '#ecfeff', color: '#155e75', border: '1px solid #a5f3fc',
                borderRadius: 20, padding: '3px 10px', fontSize: 11, fontWeight: 700,
              }}>
                Total — Rev. Final
              </span>
            )}
            {/* "Aprobado por Administrador" badge removed (V26) — its trigger
                (reviewedBy !== supervisorId) fired on every clerk's normal final
                approval. The takeover badge below now handles all outside-of-role
                attribution and preserves history across subsequent actions. */}
            {receiptMeta?.lastActorUserId
              && receiptMeta.lastActorName
              && receiptMeta.takeoverTargetName && (
              <span style={{
                background: '#fef3c7', color: '#92400e', border: '1px solid #fde68a',
                borderRadius: 20, padding: '3px 10px', fontSize: 11, fontWeight: 600,
              }}>
                Acción por {receiptMeta.lastActorName} en nombre de {receiptMeta.takeoverTargetName}
              </span>
            )}
          </div>
          <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>
            {employeeName && (
              <span style={{ marginRight: 8 }}>
                <span style={{ color: '#94a3b8' }}>Sometido por:</span> <span style={{ color: NAVY, fontWeight: 500 }}>{employeeName}</span>
              </span>
            )}
            {receiptDate && <span style={{ marginRight: 8 }}>| {receiptDate}</span>}
            {paymentMethod && (
              <span style={{ marginRight: 8 }}>
                | {PAYMENT_METHODS[paymentMethod] || paymentMethod}
                {group.cardType && ` (${group.cardType === 'amex' ? 'American Express' : group.cardType === 'home_depot' ? 'Home Depot' : group.cardType === 'mastercard' ? 'Mastercard' : group.cardType === 'total' ? 'Total' : group.cardType === 'visa' ? 'Visa' : group.cardType})`}
              </span>
            )}
            {projectNames.length > 0 && <span style={{ marginRight: 8 }}>| {projectNames.join(', ')}</span>}
            {reviewerName && ['approved', 'paid'].includes(groupStatus) && (
              <span>
                | <span style={{ color: '#94a3b8' }}>Aprobado por:</span> <span style={{ color: '#16a34a', fontWeight: 500 }}>{reviewerName}</span>
              </span>
            )}
          </div>
        </div>
      </div>

      {/* V29.1: read-only banner for finalized receipts */}
      {isFinalized && (
        <div style={{
          marginBottom: 14, padding: '10px 16px', borderRadius: 12,
          background: '#f1f5f9', border: '1px solid #cbd5e1',
          display: 'flex', alignItems: 'center', gap: 10,
          fontSize: 13, color: '#1e293b',
        }}>
          <span style={{ fontSize: 16 }}>🔒</span>
          <span>
            <strong style={{ color: NAVY }}>Recibo finalizado — solo lectura.</strong>{' '}
            <span style={{ color: '#64748b' }}>
              No es posible modificar campos una vez aprobado o pagado. Si necesita corregir algo, contacte un administrador.
            </span>
          </span>
        </div>
      )}

      {/* Approval progress tracker */}
      {group.receiptStatus !== 'draft' && (
        <div style={{
          ...bubbleStyle, padding: '10px 16px', marginBottom: 14,
          display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap',
          background: '#f8fafc',
        }}>
          <span style={{ fontSize: 12, color: '#64748b', marginRight: 4 }}>Aprobación:</span>
          {(() => {
            const st = group.receiptStatus || 'pending';
            const done = (n) => <span key={n} style={{ fontSize: 11, color: '#16a34a', fontWeight: 600 }}>{n} ✓</span>;
            const cur = (n) => <span key={n} style={{ fontSize: 11, fontWeight: 700, color: '#d97706', padding: '1px 6px', background: '#fef3c7', borderRadius: 6 }}>{n}</span>;
            const pend = (n) => <span key={n} style={{ fontSize: 11, color: '#94a3b8' }}>{n}</span>;
            const ret = (n) => <span key={n} style={{ fontSize: 11, color: '#ea580c', fontWeight: 600 }}>{n} ↩</span>;
            const rej = (n) => <span key={n} style={{ fontSize: 11, color: '#ef4444', fontWeight: 600 }}>{n} ✗</span>;
            const arr = (i) => <span key={`a${i}`} style={{ color: '#cbd5e1', fontSize: 11 }}> → </span>;
            const steps = [];
            if (['pending_review', 'approved', 'paid'].includes(st)) steps.push(done('Ingeniero(a)'));
            else if (['pending', 'partially_returned'].includes(st)) steps.push(cur('Ingeniero(a)'));
            else if (st === 'sent_back') steps.push(ret('Ingeniero(a)'));
            else if (st === 'rejected') steps.push(rej('Ingeniero(a)'));
            else steps.push(pend('Ingeniero(a)'));
            steps.push(arr(1));
            if (['approved', 'paid'].includes(st)) steps.push(done('Rev. Final'));
            else if (st === 'pending_review') steps.push(cur('Rev. Final'));
            else steps.push(pend('Rev. Final'));
            return steps;
          })()}
        </div>
      )}

      {/* Receipt info card with image */}
      <div style={{ ...bubbleStyle, marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          {/* Receipt image */}
          <div
            onClick={() => group.items[0] && setViewingItem(group.items[0])}
            title="Ver recibo en grande"
            style={{
              width: 100, height: 100, borderRadius: 10, overflow: 'hidden',
              border: `1px solid ${BORDER_COLOR}`, background: '#f1f5f9',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', flexShrink: 0,
            }}
          >
            {previewUrl && fileType?.startsWith('image/') ? (
              <AuthedReceiptPreview src={previewUrl} fileType={fileType} alt="" imgStyle={{ width: '100%', height: '100%', objectFit: 'cover' }} style={{ width: '100%', height: '100%' }} />
            ) : (
              <FileText size={32} style={{ color: '#94a3b8' }} />
            )}
          </div>

          {/* Receipt fields */}
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr 1fr', gap: 12 }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#64748b', marginBottom: 4 }}>Proveedor</div>
                <div style={{ fontSize: 14, fontWeight: 600, color: NAVY }}>{vendor || '—'}</div>
              </div>
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#64748b', marginBottom: 4 }}>Chofer</div>
                <div style={{ fontSize: 14, color: NAVY }}>{employeeName || '—'}</div>
              </div>
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#64748b', marginBottom: 4 }}>Proyectos</div>
                <div style={{ fontSize: 14, color: NAVY }}>
                  {projectNames.length > 0 ? projectNames.join(', ') : '—'}
                </div>
              </div>
              {(() => {
                // Fixed receipt total from AI extraction — never changes
                const fixedTotal = group.receiptAmount || 0;
                const myT = group.items.reduce((s, i) => { const a = parseFloat(i.amount) || 0; return s + a + a * (parseFloat(i.taxRate) || 0) / 100; }, 0);
                const otherT = otherSupervisorItems.reduce((s, i) => { const a = parseFloat(i.amount) || 0; return s + a + a * (parseFloat(i.taxRate) || 0) / 100; }, 0);
                const assigned = fixedTotal - otherT;
                const matches = fixedTotal <= 0 || Math.abs(myT - assigned) <= 0.02;
                return (
                  <>
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 600, color: '#64748b', marginBottom: 4 }}>Total del Recibo</div>
                      <div style={{ fontSize: 15, fontWeight: 700, color: '#64748b' }}>
                        {fmt(fixedTotal)}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 600, color: '#64748b', marginBottom: 4 }}>Total Asignado</div>
                      <div style={{ fontSize: 17, fontWeight: 700, color: AMBER, background: '#fffbeb', padding: '4px 10px', borderRadius: 8, border: `1px solid #fcd34d`, display: 'inline-block' }}>
                        {fmt(assigned)}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 600, color: '#64748b', marginBottom: 4 }}>Mis Items</div>
                      <div style={{ fontSize: 17, fontWeight: 700, color: matches ? NAVY : '#dc2626' }}>
                        {fmt(myT)}
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      </div>

      {/* Reviewer notice — shown when receipt is NOT yet at pending_review AND reviewer has NO pending items assigned */}
      {isReviewer && !adminMode && !hasPendingItems && group.receiptStatus !== 'pending_review' && group.receiptStatus !== 'approved' && group.receiptStatus !== 'rejected' && (
        <div style={{
          ...bubbleStyle, marginBottom: 16, padding: '10px 18px',
          background: '#f1f5f9', border: '1px solid #cbd5e1',
          display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#475569',
        }}>
          <AlertTriangle size={14} style={{ flexShrink: 0, color: '#64748b' }} />
          <span>Pendiente de Aprobación del Supervisor — Solo lectura hasta que el supervisor complete su revisión.</span>
        </div>
      )}

      {/* Action bar — L1 approve (supervisor, engineer, or reviewer with pending admin items) */}
      {/* NOT shown for reviewer when receipt is pending_review (reviewer final bar handles that) */}
      {hasPendingItems && !(isReviewer && group.receiptStatus === 'pending_review') && (
        <div style={{
          ...bubbleStyle,
          marginBottom: 16,
          padding: '12px 18px',
          display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
          background: '#f0f4ff',
        }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: NAVY, marginRight: 4 }}>
            Acciones ({pendingItems.length} item{pendingItems.length !== 1 ? 's' : ''} pendiente{pendingItems.length !== 1 ? 's' : ''}):
          </span>
          {actionLoading ? (
            <span style={{ fontSize: 12, color: '#64748b', display: 'flex', alignItems: 'center', gap: 5 }}>
              <Loader2 size={13} className="spin" /> Procesando…
            </span>
          ) : (
            <>
              <button onClick={handleReceiptApprove} style={actionBtnStyle('#166534')}>
                <Check size={12} /> Aprobar Mi Sección
              </button>
              <button onClick={handleReceiptSendBack} style={actionBtnStyle('#d97706')}>
                <CornerUpLeft size={12} /> Devolver Mi Sección
              </button>
              <button onClick={handleReceiptReject} style={actionBtnStyle('#dc2626')}>
                <X size={12} /> Rechazar Mi Sección
              </button>
            </>
          )}
        </div>
      )}

      {/* Reviewer action bar — shown when receipt is pending_review (reviewer or admin) */}
      {(isReviewer || adminMode) && group.receiptStatus === 'pending_review' && (
        <div style={{
          ...bubbleStyle,
          marginBottom: 16,
          padding: '12px 18px',
          display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
          background: '#E8EAF6',
        }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: '#283593', marginRight: 4 }}>
            Revisión Final:
          </span>
          {actionLoading ? (
            <span style={{ fontSize: 12, color: '#64748b', display: 'flex', alignItems: 'center', gap: 5 }}>
              <Loader2 size={13} className="spin" /> Procesando…
            </span>
          ) : (
            <>
              <button onClick={handleReviewerApprove} style={actionBtnStyle('#283593')}>
                <Check size={12} /> Aprobar Recibo
              </button>
              <button onClick={handleReviewerSendBack} style={actionBtnStyle('#d97706')}>
                <CornerUpLeft size={12} /> Devolver a Supervisor
              </button>
            </>
          )}
        </div>
      )}

      {/* V29.1: Contabilidad Sage — compact strip below the action bar.
          Dropdown catalog switches by payment_method (credit_card vs petty_cash).
          LOCKED once the receipt is approved or paid — corrections require send-back. */}
      {(isReviewer || adminMode) && (() => {
        const isPettyCash = paymentMethod === 'petty_cash' || paymentMethod === 'efectivo';
        const vendorList = isPettyCash ? pettyCashVendors : creditCardVendors;
        const items = vendorList.map(v => ({ id: v.vendor_id, label: v.vendor_id, sublabel: v.vendor_name }));
        const isLocked = ['approved', 'paid'].includes(group.receiptStatus);
        return (
          <div style={{
            ...bubbleStyle, marginBottom: 12, padding: '8px 14px',
            display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
            background: isLocked ? '#f8fafc' : undefined,
          }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: NAVY, whiteSpace: 'nowrap' }}>
              Proveedor Sage:
            </span>
            {isLocked ? (
              sageVendorId
                ? <span style={{ background: '#dcfce7', color: '#166534', border: '1px solid #86efac', borderRadius: 20, padding: '2px 10px', fontSize: 12, fontWeight: 600, fontFamily: 'monospace' }}>
                    ✓ {sageVendorId}
                  </span>
                : <span style={{ background: '#fee2e2', color: '#991b1b', border: '1px solid #fca5a5', borderRadius: 20, padding: '2px 10px', fontSize: 11, fontWeight: 600 }}>
                    ⚠ Sin asignar (devolver el recibo para corregir)
                  </span>
            ) : (
              <>
                <div style={{ width: 300 }}>
                  <SearchableSelect
                    items={items}
                    value={sageVendorId}
                    onChange={handleUpdateSageVendor}
                    placeholder={isPettyCash ? 'Buscar PET-...' : 'Buscar proveedor...'}
                    disabled={sageSaving}
                  />
                </div>
                {sageVendorId
                  ? <span style={{ background: '#dcfce7', color: '#166534', border: '1px solid #86efac', borderRadius: 20, padding: '1px 8px', fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap' }}>
                      ✓ {sageVendorId}
                    </span>
                  : <span style={{ background: '#fef3c7', color: '#92400e', border: '1px solid #fcd34d', borderRadius: 20, padding: '1px 8px', fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap' }}>
                      ⚠ Pendiente
                    </span>
                }
                {sageSaving && <span style={{ fontSize: 11, color: '#94a3b8' }}>Guardando…</span>}
                {sageVendorId && !sageSaving && (
                  <button
                    onClick={() => handleUpdateSageVendor(null)}
                    style={{
                      fontSize: 11, color: '#64748b',
                      background: 'none', border: 'none', cursor: 'pointer',
                      textDecoration: 'underline', fontFamily: 'Inter, sans-serif',
                      padding: 0,
                    }}
                  >
                    Quitar
                  </button>
                )}
              </>
            )}
            {isLocked && (
              <span style={{ fontSize: 10, color: '#94a3b8', fontStyle: 'italic', marginLeft: 'auto' }}>
                Bloqueado tras aprobación
              </span>
            )}
          </div>
        );
      })()}

      {/* 03/06 alert */}
      {hasAlertCodes && !dismissedAlerts.has(receiptId) && hasPendingItems && (
        <div style={{
          ...bubbleStyle,
          marginBottom: 16,
          padding: '10px 18px', background: '#fef3c7',
          border: '1px solid #fcd34d',
          display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#92400e',
        }}>
          <AlertTriangle size={14} style={{ flexShrink: 0 }} />
          <span style={{ flex: 1 }}>
            Please verify that the codes are correctly assigned based on the description and what was purchased.
          </span>
          <button onClick={dismissAlert} style={{
            background: 'none', border: 'none', cursor: 'pointer', color: '#92400e', padding: 2,
          }}>
            <X size={14} />
          </button>
        </div>
      )}

      {/* Apply GL to all — reviewer bulk action */}
      {isReviewer && group.receiptStatus === 'pending_review' && group.items.length > 1 && (
        <div style={{
          ...bubbleStyle, marginBottom: 8, padding: '8px 18px',
          display: 'flex', alignItems: 'center', gap: 10, fontSize: 12,
        }}>
          <span style={{ fontWeight: 600, color: NAVY, fontSize: 11 }}>Aplicar GL a todos:</span>
          <select
            style={{ ...selectStyle, fontSize: 11, width: 120 }}
            defaultValue=""
            onChange={(e) => {
              const code = e.target.value;
              if (!code) return;
              group.items.forEach(item => handleGlCodeChange(item.id, code));
              e.target.value = '';
            }}
          >
            <option value="">— Seleccionar —</option>
            {glCodes.map(g => <option key={g.code} value={g.code}>{g.code}</option>)}
          </select>
        </div>
      )}

      {/* Real-time mismatch warning — compare against assigned portion */}
      {(() => {
        const myT = group.items.reduce((s, i) => { const a = parseFloat(i.amount) || 0; return s + a + a * (parseFloat(i.taxRate) || 0) / 100; }, 0);
        const otherT = otherSupervisorItems.reduce((s, i) => { const a = parseFloat(i.amount) || 0; return s + a + a * (parseFloat(i.taxRate) || 0) / 100; }, 0);
        const fixedTotal = group.receiptAmount || 0;
        const assigned = fixedTotal - otherT;
        const hasMismatch = fixedTotal > 0 && Math.abs(myT - assigned) > 0.02;
        return hasMismatch ? (
          <div style={{
            ...bubbleStyle, marginBottom: 8, padding: '10px 18px',
            background: '#fef2f2', border: '1px solid #fecaca',
            display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#dc2626',
          }}>
            <AlertTriangle size={14} style={{ flexShrink: 0 }} />
            <span>
              Mis Items ({fmt(myT)}) no coincide con el Total Asignado ({fmt(assigned)}).
              Ajuste los montos o impuestos.
            </span>
          </div>
        ) : null;
      })()}

      {/* Apply same tax to all items — reviewer only (hidden when finalized) */}
      {canEditTax && !isFinalized && group.items.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, flexWrap: 'wrap' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#475569', cursor: 'pointer' }}>
            <input type="checkbox" checked={applyGlobalTax}
              onChange={(e) => setApplyGlobalTax(e.target.checked)}
              style={{ accentColor: AMBER }} />
            Aplicar mismo impuesto a todos
          </label>
          {applyGlobalTax && (
            <>
              <select
                value={globalTaxValue}
                onChange={(e) => {
                  setGlobalTaxValue(e.target.value);
                  const rate = e.target.value;
                  for (const item of group.items) {
                    handleItemFieldChange(item.id, 'taxRate', rate);
                  }
                }}
                style={{ ...fieldStyle, width: 'auto', fontSize: 12 }}
              >
                {PR_TAX_RATES.map(r => <option key={r.value} value={String(r.value)}>{r.label}</option>)}
              </select>
            </>
          )}
        </div>
      )}

      {/* ── Item-level table (supervisor, admin, reviewer all see full detail) ── */}
      {group.items.length > 0 && (
        <div style={{
          ...bubbleStyle, padding: 0, overflow: 'hidden',
          // V29.1: block all interaction with item rows once the receipt is finalized.
          // The action bars (L1 / reviewer) are already hidden by their own status guards;
          // this covers inline cell editors (GL, project, cost center, qty, amount, etc.).
          pointerEvents: isFinalized ? 'none' : 'auto',
          opacity: isFinalized ? 0.92 : 1,
        }}>
          <div className="supervisor-table" style={{ overflowX: 'auto' }}>
            {/* Table header */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: gridCols,
              background: '#f8fafc',
              fontWeight: '600', fontSize: 11, color: '#64748b',
              padding: '8px 18px', gap: 8,
              borderBottom: `1px solid ${BORDER_COLOR}`,
              letterSpacing: '0.03em',
            }}>
              <div>Descripción</div>
              <div>Tarea / GL</div>
              <div>Centro Costo</div>
              {canCodeGL && <div>Código GL</div>}
              <div style={{ textAlign: 'center' }}>Cant.</div>
              <div>Unidad</div>
              <div style={{ textAlign: 'right' }}>P. Unit.</div>
              <div style={{ textAlign: 'right' }}>Monto</div>
              {canEditTax && <div style={{ textAlign: 'right' }}>Tax%</div>}
              {canEditTax && <div style={{ textAlign: 'right' }}>Tax $</div>}
              <div style={{ textAlign: 'right' }}>Total</div>
              <div>Estado</div>
              <div></div>
            </div>

            {/* Items grouped by project */}
            {projectGroups.map(([projectName, projectItems]) => {
              // When the receipt has a final status (approved/rejected), use it for each project section
              const finalStatus = ['approved', 'rejected', 'sent_back'].includes(group.receiptStatus) ? group.receiptStatus : null;
              const projectStatus = finalStatus || deriveGroupStatus(projectItems);
              return (
              <div key={projectName}>
                <div style={{
                  padding: '8px 18px', background: '#f0f4ff',
                  fontWeight: 600, fontSize: 13, color: NAVY,
                  borderBottom: `1px solid ${BORDER_COLOR}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                }}>
                  <span>{projectName}</span>
                  <StatusBadge status={projectStatus} small />
                </div>

                {projectItems.map(item => renderItemRow(item))}

                {renderProjectSummary(projectItems)}
              </div>
              );
            })}

            {/* Add Line button */}
            {hasPendingItems && (() => {
              const otherTotal2 = otherSupervisorItems.reduce((s, i) => { const a=parseFloat(i.amount)||0; return s+a+a*(parseFloat(i.taxRate)||0)/100; }, 0);
              const allItemsTotal = totalAmount + otherTotal2;
              // Use max(stored, computed) so old receipts (stored without tax) don't falsely disable
              const effectiveAmt = Math.max(group.receiptAmount || 0, allItemsTotal);
              const addDisabled = effectiveAmt > 0 && allItemsTotal >= effectiveAmt;
              return (
              <div style={{
                padding: '10px 18px', borderTop: `1px solid ${BORDER_COLOR}`,
                display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: 10,
              }}>
                <button
                  onClick={() => !addDisabled && handleAddItem(group)}
                  disabled={addDisabled}
                  style={{
                    background: 'none', border: `1px dashed ${BORDER_COLOR}`,
                    borderRadius: 6, padding: '6px 12px', fontSize: 12, fontWeight: 600,
                    color: addDisabled ? '#94a3b8' : '#475569',
                    cursor: addDisabled ? 'not-allowed' : 'pointer',
                    fontFamily: 'Inter, sans-serif',
                    display: 'flex', alignItems: 'center', gap: 4,
                    opacity: addDisabled ? 0.6 : 1,
                  }}
                >
                  <Plus size={13} /> Agregar Item
                </button>
                {addDisabled && (
                  <span style={{ fontSize: 11, color: '#94a3b8', fontStyle: 'italic' }}>
                    El total de items alcanza el total del recibo ({fmt(effectiveAmt)})
                  </span>
                )}
              </div>
              );
            })()}
          </div>
        </div>
      )}

      {/* Other supervisors' sections (read-only, supervisor/admin only) */}
      {!adminMode && !isReviewer && otherSupervisorGroups.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            Otros Supervisores
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {otherSupervisorGroups.map(sg => {
              const isExpanded = expandedOtherSections.has(sg.supervisorId);
              const sgStatus = ['approved','rejected','sent_back'].includes(group.receiptStatus) ? group.receiptStatus : deriveGroupStatus(sg.items);
              const sgTotal = sg.items.reduce((s, i) => { const a=parseFloat(i.amount)||0; return s+a+a*(parseFloat(i.taxRate)||0)/100; }, 0);
              const Icon = isExpanded ? ChevronUp : ChevronDown;
              return (
                <div key={sg.supervisorId} style={{ ...bubbleStyle, padding: 0, overflow: 'hidden', opacity: 0.75 }}>
                  {/* Collapsible header */}
                  <button
                    onClick={() => toggleOtherSection(sg.supervisorId)}
                    style={{
                      width: '100%', display: 'flex', alignItems: 'center',
                      padding: '10px 18px', background: '#f8fafc',
                      border: 'none', borderBottom: isExpanded ? `1px solid ${BORDER_COLOR}` : 'none',
                      cursor: 'pointer', fontFamily: 'Inter, sans-serif',
                    }}
                  >
                    <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontWeight: 600, fontSize: 13, color: NAVY }}>{sg.supervisorName}</span>
                      <StatusBadge status={sgStatus} small />
                      <span style={{ fontSize: 11, color: '#94a3b8', fontStyle: 'italic', marginLeft: 4 }}>Solo Lectura</span>
                    </div>
                    <span style={{ fontWeight: 700, fontSize: 13, color: NAVY, marginRight: 10 }}>{fmt(sgTotal)}</span>
                    <span style={{ fontSize: 11, color: '#94a3b8', marginRight: 8 }}>
                      {sg.items.length} item{sg.items.length !== 1 ? 's' : ''}
                    </span>
                    <Icon size={14} style={{ color: '#94a3b8', flexShrink: 0 }} />
                  </button>

                  {/* Expanded content */}
                  {isExpanded && (
                    <div>
                      {/* Table header */}
                      <div style={{
                        display: 'grid', gridTemplateColumns: '1fr 1fr 40px 50px 65px 80px 55px 60px 80px 80px',
                        background: '#f8fafc', fontWeight: 600, fontSize: 11, color: '#64748b',
                        padding: '6px 18px', gap: 8, borderBottom: `1px solid ${BORDER_COLOR}`,
                        letterSpacing: '0.03em',
                      }}>
                        <div>Descripción</div>
                        <div>Proyecto</div>
                        <div style={{ textAlign: 'center' }}>Cant.</div>
                        <div>Unidad</div>
                        <div style={{ textAlign: 'right' }}>P. Unit.</div>
                        <div style={{ textAlign: 'right' }}>Monto</div>
                        <div style={{ textAlign: 'right' }}>Tax%</div>
                        <div style={{ textAlign: 'right' }}>Tax $</div>
                        <div style={{ textAlign: 'right' }}>Total</div>
                        <div style={{ textAlign: 'center' }}>Estado</div>
                      </div>

                      {/* Items */}
                      {sg.items.map(item => {
                        const a = parseFloat(item.amount) || 0;
                        const taxAmt = a * (parseFloat(item.taxRate) || 0) / 100;
                        return (
                        <div key={item.id} style={{
                          display: 'grid', gridTemplateColumns: '1fr 1fr 40px 50px 65px 80px 55px 60px 80px 80px',
                          padding: '8px 18px', gap: 8,
                          borderBottom: '1px solid #f1f5f9',
                          alignItems: 'center', background: '#fff',
                        }}>
                          <div style={{ fontWeight: 600, fontSize: 12, color: '#1e293b' }}>
                            {item.description || '—'}
                          </div>
                          <div style={{ fontSize: 12, color: '#64748b' }}>
                            {item.projectName || '—'}
                          </div>
                          <div style={{ textAlign: 'center', fontSize: 12, color: '#475569' }}>
                            {item.quantity || 1}
                          </div>
                          <div style={{ fontSize: 11, color: '#64748b' }}>
                            {item.unit || '—'}
                          </div>
                          <div style={{ fontSize: 12, color: '#64748b', textAlign: 'right' }}>
                            {fmt((parseFloat(item.amount) || 0) / (item.quantity || 1))}
                          </div>
                          <div style={{ fontWeight: 700, fontSize: 12, color: '#1e293b', textAlign: 'right' }}>
                            {fmt(item.amount)}
                          </div>
                          <div style={{ fontSize: 12, color: '#64748b', textAlign: 'right' }}>
                            {item.taxRate > 0 ? `${item.taxRate}%` : '—'}
                          </div>
                          <div style={{ fontSize: 12, color: '#64748b', textAlign: 'right' }}>
                            {taxAmt > 0 ? fmt(taxAmt) : '—'}
                          </div>
                          <div style={{ fontWeight: 700, fontSize: 12, color: NAVY, textAlign: 'right' }}>
                            {fmt(a + taxAmt)}
                          </div>
                          <div style={{ textAlign: 'center' }}>
                            <StatusBadge status={item.status} small />
                          </div>
                        </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Modals */}
      {viewingItem && viewerReceipt && (
        <ReceiptViewerModal
          receipt={viewerReceipt}
          hasPrev={false}
          hasNext={false}
          onPrev={() => {}}
          onNext={() => {}}
          onClose={() => setViewingItem(null)}
        />
      )}
      {rejectTarget && (
        <RejectModal
          receipt={rejectTarget}
          onConfirm={handleConfirmReject}
          onCancel={() => setRejectTarget(null)}
        />
      )}
      {sendBackTarget && (
        <SendBackModal
          receipt={sendBackTarget}
          onConfirm={handleConfirmSendBack}
          onCancel={() => setSendBackTarget(null)}
        />
      )}

      {/* Split Modal */}
      {splitItem && (() => {
        const origAmt = parseFloat(splitItem.amount) || 0;
        const splitTotal = splitRows.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
        const diff = splitTotal - origAmt;
        const isValid = Math.abs(diff) <= 0.01 && splitRows.every(r => r.costCenter);
        const ccOptions = (itemCostCenters[`${splitItem.projectId}-${splitItem.selectedTaskId}`] || allCostCenters);

        return (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            onClick={() => setSplitItem(null)}>
            <div style={{ background: '#fff', borderRadius: 16, padding: '24px 28px', maxWidth: 520, width: '90%', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}
              onClick={e => e.stopPropagation()}>
              <h3 style={{ fontSize: 16, fontWeight: 700, color: NAVY, margin: '0 0 4px' }}>Dividir Item</h3>
              <div style={{ fontSize: 12, color: '#64748b', marginBottom: 16 }}>
                "{splitItem.description}" — Monto original: <strong>{fmt(origAmt)}</strong>
              </div>

              {splitRows.map((row, idx) => {
                const usedCCs = splitRows.filter((_, j) => j !== idx).map(r => r.costCenter).filter(Boolean);
                const availableCCs = ccOptions.filter(cc => !usedCCs.includes(cc.name) || cc.name === row.costCenter);
                return (
                  <div key={idx} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
                    <div style={{ width: 70 }}>
                      <input type="number" step="1" min="0" max="100" value={row.pct}
                        onChange={e => {
                          const pct = parseFloat(e.target.value) || 0;
                          const amt = Math.round(origAmt * pct) / 100;
                          setSplitRows(prev => prev.map((r, i) => i === idx ? { ...r, pct, amount: amt } : r));
                        }}
                        style={{ ...fieldStyle, fontSize: 12, textAlign: 'center', width: '100%' }} />
                      <div style={{ fontSize: 9, color: '#94a3b8', textAlign: 'center' }}>%</div>
                    </div>
                    <div style={{ width: 100 }}>
                      <input type="number" step="0.01" min="0" value={row.amount}
                        onChange={e => {
                          const amt = parseFloat(e.target.value) || 0;
                          const pct = origAmt > 0 ? Math.round(amt / origAmt * 10000) / 100 : 0;
                          setSplitRows(prev => prev.map((r, i) => i === idx ? { ...r, amount: amt, pct } : r));
                        }}
                        style={{ ...fieldStyle, fontSize: 12, textAlign: 'right', width: '100%' }} />
                      <div style={{ fontSize: 9, color: '#94a3b8', textAlign: 'center' }}>Monto</div>
                    </div>
                    <div style={{ flex: 1 }}>
                      <select value={row.costCenter}
                        onChange={e => setSplitRows(prev => prev.map((r, i) => i === idx ? { ...r, costCenter: e.target.value } : r))}
                        style={{ ...selectStyle, fontSize: 11, width: '100%' }}>
                        <option value="">— Centro de Costo —</option>
                        {availableCCs.map(cc => <option key={cc.id} value={cc.name}>{cc.name}</option>)}
                      </select>
                    </div>
                    {splitRows.length > 2 && (
                      <button onClick={() => setSplitRows(prev => prev.filter((_, i) => i !== idx))}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', padding: 4 }}>✕</button>
                    )}
                  </div>
                );
              })}

              {/* Add row + total + difference */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8, paddingTop: 8, borderTop: `1px solid ${BORDER_COLOR}` }}>
                <button
                  onClick={() => setSplitRows(prev => [...prev, { pct: 0, amount: 0, costCenter: '' }])}
                  disabled={splitRows.length >= ccOptions.length}
                  style={{
                    background: 'none', border: `1px solid ${BORDER_COLOR}`, borderRadius: 6,
                    padding: '4px 10px', fontSize: 11, cursor: splitRows.length >= ccOptions.length ? 'not-allowed' : 'pointer',
                    color: '#475569', opacity: splitRows.length >= ccOptions.length ? 0.4 : 1,
                  }}>
                  + Agregar fila
                </button>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: Math.abs(diff) > 0.01 ? '#dc2626' : '#16a34a' }}>
                    Total: {fmt(splitTotal)} / {fmt(origAmt)}
                  </div>
                  {Math.abs(diff) > 0.01 && (
                    <div style={{ fontSize: 11, color: '#dc2626', marginTop: 2 }}>
                      {diff > 0 ? `Excede ${fmt(diff)}` : `Faltan ${fmt(Math.abs(diff))}`}
                    </div>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
                <button onClick={() => setSplitItem(null)} style={outlineBtn}>Cancelar</button>
                <button
                  disabled={!isValid}
                  onClick={async () => {
                    // First row: update the original item's amount and cost center
                    handleItemFieldChange(splitItem.id, 'amount', splitRows[0].amount);
                    handleItemFieldChange(splitItem.id, 'costCenter', splitRows[0].costCenter);
                    // Remaining rows: create new split items with actual amounts
                    for (let i = 1; i < splitRows.length; i++) {
                      await handleSplitItem(splitItem, {
                        splitAmount: splitRows[i].amount,
                        splitCostCenter: splitRows[i].costCenter,
                        parentId: splitItem.id,
                        origAmt: origAmt,
                      });
                    }
                    setSplitItem(null);
                  }}
                  style={{ ...navyBtn, opacity: isValid ? 1 : 0.5 }}
                >
                  Aplicar División
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
